import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ImportExportService from '../Services/importExport.service';

const usePriorityImport = ({ client, token, fetchEnums, setLoading }) => {
    const { t } = useTranslation();

    // Account Cards
    const previewPriorityAccountCards = useCallback(async (file) => {
        if (!file || !client) return;
        try {
            const res = await ImportExportService.PreviewPriorityAccountCards(
                client.caseId,
                client.accountantId,
                file,
                token
            );
            toast.success(t('PreviewSuccess'));
            return res;
        } catch (err) {
            console.error('Priority account cards preview failed:', err);
            toast.error(t('PreviewFailed'));
            throw err;
        }
    }, [client, token, t]);

    const commitPriorityAccountCards = useCallback(async (file) => {
        if (!file || !client) return;
        try {
            setLoading(true);
            const res = await ImportExportService.ImportPriorityAccountCards(
                client.caseId,
                file,
                token
            );

            const processedCount = res?.processedCount ?? res?.[0] ?? 0;
            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Priority account cards import errors:', errorMessages);
                toast.warning(
                    t('PriorityAccountCardsUploadedWithErrors', {
                        processedCount,
                        errorCount,
                        firstError: errorMessages[0]
                    })
                );
            } else {
                toast.success(
                    t('PriorityAccountCardsUploadedSuccessfully', {
                        processedCount
                    })
                );
            }

            await fetchEnums(token, client, true);
            return res;
        } catch (err) {
            console.error('Priority account cards import error:', err);
            toast.error(t('PriorityAccountCardsUploadFailed'));
            throw err;
        } finally {
            setLoading(false);
        }
    }, [client, token, t, fetchEnums, setLoading]);

    // Journal Entries
    const previewPriorityJournal = useCallback(async (journalFile, transactionTypesFile) => {
        if (!journalFile || !client) return;
        try {
            const res = await ImportExportService.PreviewPriorityJournal(
                client.caseId,
                client.accountantId,
                journalFile,
                transactionTypesFile,
                token
            );
            toast.success(t('PreviewSuccess'));
            return res;
        } catch (err) {
            console.error('Priority journal preview failed:', err);
            toast.error(t('PreviewFailed'));
            throw err;
        }
    }, [client, token, t]);

    const commitPriorityJournal = useCallback(async (journalFile, transactionTypesFile) => {
        if (!journalFile || !client) return;
        try {
            setLoading(true);
            const res = await ImportExportService.ImportPriorityJournal(
                client.caseId,
                journalFile,
                transactionTypesFile,
                token
            );

            const processedCount = res?.processedCount ?? res?.[0] ?? 0;
            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Priority journal import errors:', errorMessages);
                toast.warning(
                    t('PriorityJournalUploadedWithErrors', {
                        processedCount,
                        errorCount,
                        firstError: errorMessages[0]
                    })
                );
            } else {
                toast.success(
                    t('PriorityJournalUploadedSuccessfully', {
                        processedCount
                    })
                );
            }

            await fetchEnums(token, client, true);
            return res;
        } catch (err) {
            console.error('Priority journal import error:', err);
            toast.error(t('PriorityJournalUploadFailed'));
            throw err;
        } finally {
            setLoading(false);
        }
    }, [client, token, t, fetchEnums, setLoading]);

    // Opening Balance
    const previewPriorityOpeningBalance = useCallback(async (file, month, year) => {
        if (!file || !client) return;
        try {
            const res = await ImportExportService.PreviewPriorityOpeningBalance(
                client.caseId,
                client.accountantId,
                file,
                month,
                year,
                token
            );
            toast.success(t('PreviewSuccess'));
            return res;
        } catch (err) {
            console.error('Priority opening balance preview failed:', err);
            toast.error(t('PreviewFailed'));
            throw err;
        }
    }, [client, token, t]);

    const commitPriorityOpeningBalance = useCallback(async (file, month, year) => {
        if (!file || !client) return;
        try {
            setLoading(true);
            const res = await ImportExportService.ImportPriorityOpeningBalance(
                client.caseId,
                file,
                month,
                year,
                token
            );

            const processedCount = res?.processedCount ?? res?.[0] ?? 0;
            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Priority opening balance import errors:', errorMessages);
                toast.warning(
                    t('PriorityOpeningBalanceUploadedWithErrors', {
                        processedCount,
                        errorCount,
                        firstError: errorMessages[0]
                    })
                );
            } else {
                toast.success(
                    t('PriorityOpeningBalanceUploadedSuccessfully', {
                        processedCount
                    })
                );
            }

            await fetchEnums(token, client, true);
            return res;
        } catch (err) {
            console.error('Priority opening balance import error:', err);
            toast.error(t('PriorityOpeningBalanceUploadFailed'));
            throw err;
        } finally {
            setLoading(false);
        }
    }, [client, token, t, fetchEnums, setLoading]);

    return {
        previewPriorityAccountCards,
        commitPriorityAccountCards,
        previewPriorityJournal,
        commitPriorityJournal,
        previewPriorityOpeningBalance,
        commitPriorityOpeningBalance,
    };
};

export default usePriorityImport;

